import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { applyMovePayload } from 'utils/reorder';
import STORE_KEYS from '../store.keys';
import STORE_MUTATIONS from '../store.mutation';
import STORE_QUERIES from '../store.queries';

export const useFetchStore = (options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: STORE_KEYS.STORE_ME(),
    queryFn: () => STORE_QUERIES.FETCH_STORE(),
    enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchPageModules = (options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: STORE_KEYS.STORE_PAGE_MODULES(),
    queryFn: () => STORE_QUERIES.FETCH_PAGE_MODULES(),
    enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchRetailerPageAvailableLooks = (uid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: STORE_KEYS.STORE_AVAILABLE_LOOKS(uid),
    queryFn: () => STORE_QUERIES.FETCH_RETAILER_PAGE_AVAILABLE_LOOKS(uid),
    enabled: !!uid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchRetailerPageAvailableBanners = (uid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: STORE_KEYS.STORE_AVAILABLE_BANNERS(uid),
    queryFn: () => STORE_QUERIES.FETCH_RETAILER_PAGE_AVAILABLE_BANNERS(uid),
    enabled: !!uid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchRetailerPageAvailableVideos = (uid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: STORE_KEYS.STORE_AVAILABLE_VIDEOS(uid),
    queryFn: () => STORE_QUERIES.FETCH_RETAILER_PAGE_AVAILABLE_VIDEOS(uid),
    enabled: !!uid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchRetailerPageAvailableParticipants = (uid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: STORE_KEYS.STORE_AVAILABLE_PARTICIPANTS(uid),
    queryFn: () => STORE_QUERIES.FETCH_RETAILER_PAGE_AVAILABLE_PARTICIPANTS(uid),
    enabled: !!uid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchRetailerPageAvailableEvents = (uid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: STORE_KEYS.STORE_AVAILABLE_EVENTS(uid),
    queryFn: () => STORE_QUERIES.FETCH_RETAILER_PAGE_AVAILABLE_EVENTS(uid),
    enabled: !!uid && enabled,
    staleTime: 1 * 60 * 1000,
    ...queryOptions,
  });
};

export const useUpdateStore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: STORE_MUTATIONS.UPDATE_STORE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORE_KEYS.STORE_ME() });
    },
  });
};

export const useAddPageModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: STORE_MUTATIONS.ADD_PAGE_MODULE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORE_KEYS.STORE_PAGE_MODULES() });
    },
  });
};

export const useUpdatePageModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: STORE_MUTATIONS.UPDATE_PAGE_MODULE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORE_KEYS.STORE_PAGE_MODULES() });
    },
  });
};

export const useDeletePageModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: STORE_MUTATIONS.REMOVE_PAGE_MODULE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORE_KEYS.STORE_PAGE_MODULES() });
    },
  });
};

export const useUpdatePageModuleOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: STORE_MUTATIONS.MOVE_PAGE_MODULE,
    onMutate: (move) => {
      queryClient.cancelQueries({ queryKey: STORE_KEYS.STORE_PAGE_MODULES() });

      const previousModules = queryClient.getQueryData(STORE_KEYS.STORE_PAGE_MODULES());

      queryClient.setQueryData(STORE_KEYS.STORE_PAGE_MODULES(), (old) => {
        if (!old) return old;
        return applyMovePayload(old, move);
      });

      return { previousModules };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousModules) {
        queryClient.setQueryData(STORE_KEYS.STORE_PAGE_MODULES(), context.previousModules);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: STORE_KEYS.STORE_PAGE_MODULES() });
    },
  });
};
